import axiosInstance from '../../services/api/axiosConfig';
import type {
  AiOp,
  AiResult,
  AiTask,
  GenerateEstimate,
  GenerateKind,
  Timeline,
  VideoEdit,
  VideoEditAsset,
  VideoEditExport,
  VideoEditLibrary,
  VideoEditVersionSummary,
} from './types';
import { stripForSave } from './timelineOps';

/** 編集・かんたん作成の作品の ID（サーバーの UUID）。URL から来た文字列はこれで検証してからパスに入れる */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const GENERATE_KINDS: readonly GenerateKind[] = ['video', 'image', 'voice', 'bgm'];

export const isVideoEditId = (value: unknown): value is string => typeof value === 'string' && UUID.test(value);

/**
 * パスに埋め込む値の検証（任意の文字列で `../` を含む別のエンドポイントを叩かせない）。
 * 不正なら投げて、リクエストは送らない。
 */
function uuid(value: string): string {
  if (!isVideoEditId(value)) throw new Error('invalid id');
  return value;
}

function int(value: number): number {
  if (!Number.isSafeInteger(value) || value <= 0) throw new Error('invalid id');
  return value;
}

function generateKind(value: GenerateKind): GenerateKind {
  if (!GENERATE_KINDS.includes(value)) throw new Error('invalid kind');
  return value;
}

/**
 * 本格編集の API（backend routes/api/video_edit.php）。
 */
export const videoEditApi = {
  async get(id: string): Promise<VideoEdit> {
    const res = await axiosInstance.get(`/video-edits/${uuid(id)}`);
    return res.data.data;
  },

  async fromEasyVideo(projectId: string, chatId?: number | null): Promise<VideoEdit> {
    const res = await axiosInstance.post(`/video-edits/from-easy-video/${uuid(projectId)}`, { chat_id: chatId ?? undefined });
    return res.data.data;
  },

  /**
   * 動画レシピの完成動画から編集を開く。前に作った編集があればそれを返し、その後に動画レシピで作り直していれば source_stale。
   * fresh=true なら前の編集を使わずに最新の動画から作り直す。
   */
  async fromRecipeVideo(projectId: number, fresh?: boolean): Promise<VideoEdit & { source_stale: boolean }> {
    const res = await axiosInstance.post(`/video-edits/from-recipe-video/${int(projectId)}`, { fresh: fresh || undefined });
    return res.data.data;
  },

  async fromVideo(videoId: number, chatId?: number | null): Promise<VideoEdit> {
    const res = await axiosInstance.post(`/video-edits/from-video/${int(videoId)}`, { chat_id: chatId ?? undefined });
    return res.data.data;
  },

  /** 保存（楽観ロック）。409 は呼び出し側で「別の画面で保存済み」として扱う */
  async save(id: string, timeline: Timeline, version: number, title?: string): Promise<{ version: number; title: string; updated_at: string }> {
    const res = await axiosInstance.put(`/video-edits/${uuid(id)}`, { timeline: stripForSave(timeline), version, title });
    return res.data.data;
  },

  async versions(id: string): Promise<VideoEditVersionSummary[]> {
    const res = await axiosInstance.get(`/video-edits/${uuid(id)}/versions`);
    return res.data.data;
  },

  async restore(id: string, versionId: number): Promise<VideoEdit> {
    const res = await axiosInstance.post(`/video-edits/${uuid(id)}/versions/${int(versionId)}/restore`);
    return res.data.data;
  },

  async startExport(id: string, resolution: '1080p' | '720p'): Promise<VideoEditExport> {
    const res = await axiosInstance.post(`/video-edits/${uuid(id)}/export`, { resolution });
    return res.data.data;
  },

  async exportStatus(id: string): Promise<VideoEditExport> {
    const res = await axiosInstance.get(`/video-edits/${uuid(id)}/export`);
    return res.data.data;
  },

  async assets(id: string): Promise<VideoEditAsset[]> {
    const res = await axiosInstance.get(`/video-edits/${uuid(id)}/assets`);
    return res.data.data;
  },

  async library(id: string): Promise<VideoEditLibrary> {
    const res = await axiosInstance.get(`/video-edits/${uuid(id)}/library`);
    return res.data.data;
  },

  async estimate(id: string, kind: GenerateKind, durationSeconds?: number): Promise<GenerateEstimate> {
    const res = await axiosInstance.get(`/video-edits/${uuid(id)}/assets/estimate`, {
      params: { kind, duration_seconds: kind === 'video' ? durationSeconds : undefined },
    });
    return res.data.data;
  },

  /**
   * 生成（押すことが承認）。動画・画像は見積りと同じ params をそのまま送る（credit.check がそれで見積もる）。
   */
  async generate(id: string, kind: GenerateKind, prompt: string, extra: Record<string, string | number>): Promise<VideoEditAsset> {
    const res = await axiosInstance.post(`/video-edits/${uuid(id)}/assets/${generateKind(kind)}`, { prompt, ...extra });
    return res.data.data;
  },

  async upload(id: string, file: File): Promise<VideoEditAsset> {
    const form = new FormData();
    form.append('file', file);
    const res = await axiosInstance.post(`/video-edits/${uuid(id)}/assets/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 10 * 60 * 1000,
    });
    return res.data.data;
  },

  async ai(id: string, timeline: Timeline, op: AiOp | null, prompt: string | null, selectedIds: string[]): Promise<AiResult> {
    const res = await axiosInstance.post(
      `/video-edits/${uuid(id)}/ai`,
      { op: op ?? undefined, prompt: prompt ?? undefined, timeline: stripForSave(timeline), selected_ids: selectedIds },
      { timeout: 3 * 60 * 1000 },
    );
    return res.data.data;
  },

  async aiTask(id: string, taskId: string): Promise<AiTask> {
    const res = await axiosInstance.get(`/video-edits/${uuid(id)}/ai/${uuid(taskId)}`);
    return res.data.data;
  },
};

/** axios のエラーからユーザー向けの1文を取り出す */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { message?: string } } })?.response?.data;
  return data?.message || fallback;
}

export function apiErrorStatus(error: unknown): number | null {
  return (error as { response?: { status?: number } })?.response?.status ?? null;
}
